import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import {
  DataProtectionNoticeItemFileDownload,
  DataProtectionNoticeItemFileInitialState,
  DataProtectionNoticeItemFileLoad,
  DataProtectionNoticeLastItemFileLoad,
} from './data-protection-notice-item-file.actions';
import { DataProtectionNoticeItemFileService } from './data-protection-notice-item-file.service';
import { NotificationService } from '../../../../../common/services';
import { CustomHttpErrorResponse, Error, downloadBlob } from '../../../../../common';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface DataProtectionNoticeItemFileStateModel {
  dpnFileText: string;
  loaded: boolean;
}

const initialState: DataProtectionNoticeItemFileStateModel = {
  dpnFileText: null,
  loaded: false,
};

@State<DataProtectionNoticeItemFileStateModel>({
  name: 'dataProtectionNoticeItemFile',
  defaults: initialState,
})
@Injectable()
export class DataProtectionNoticeItemFileState {
  @Selector()
  static getState(state: DataProtectionNoticeItemFileStateModel): DataProtectionNoticeItemFileStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: DataProtectionNoticeItemFileStateModel): boolean {
    return state.loaded;
  }

  constructor(
    private dpnItemFileService: DataProtectionNoticeItemFileService,
    private notificationService: NotificationService,
  ) {}

  @Action(DataProtectionNoticeItemFileLoad)
  getDpnItemFile(
    ctx: StateContext<DataProtectionNoticeItemFileStateModel>,
    { payload }: DataProtectionNoticeItemFileLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.dpnItemFileService.getDpnItemFile(payload).pipe(
      map((dpnFileText: string) => {
        ctx.patchState({ dpnFileText, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DataProtectionNoticeLastItemFileLoad)
  getLastDpnItemFile(
    ctx: StateContext<DataProtectionNoticeItemFileStateModel>,
    { country }: DataProtectionNoticeLastItemFileLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.dpnItemFileService.getLastDpnItemFile(country).pipe(
      map((dpnFileText: string) => {
        ctx.patchState({ dpnFileText, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DataProtectionNoticeItemFileDownload)
  downloadDpnItemFile(
    ctx: StateContext<DataProtectionNoticeItemFileStateModel>,
    { payload }: DataProtectionNoticeItemFileDownload,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.dpnItemFileService.downloadDpnItemFile(payload.dpnFileId).pipe(
      map((response: string) => {
        const blob = new Blob([atob(response)], { type: 'text/plain' });
        downloadBlob(blob, `${payload.version}.html`);
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DataProtectionNoticeItemFileInitialState)
  setInitialState(ctx: StateContext<DataProtectionNoticeItemFileStateModel>): void {
    ctx.setState(initialState);
  }
}
